import React from 'react';
import { Crown, Plus, ShoppingCart, Calendar, MapPin, Users, Clock, BookOpen, Ticket } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { OFFICES } from '../data/database';

const ServiceCard = ({ service, showBusiness = false, onBook, compact = false, onClick }) => {
  const office = OFFICES.find(o => o.id === service.officeId);

  const handleClick = () => {
    if (onClick) onClick();
  };

  if (compact) {
    return (
      <div 
        className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100 hover:border-emerald-200 transition-all cursor-pointer"
        onClick={handleClick}
        data-testid={`service-card-${service.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-gray-900 truncate">{service.name}</h4>
            {service.isElit && <Crown className="w-4 h-4 text-amber-500 flex-shrink-0" />}
          </div>
          {showBusiness && office && (
            <p className="text-xs text-gray-500 truncate">{office?.name}</p>
          )}
          <p className="text-xs text-gray-400">{service.duration}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <p className="font-bold text-emerald-600">€{service.price}</p>
            {service.originalPrice && (
              <p className="text-xs text-gray-400 line-through">€{service.originalPrice}</p>
            )}
          </div>
          <button 
            onClick={(e) => { e.stopPropagation(); onBook && onBook(service); }}
            className="px-3 py-1.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-colors"
          >
            Prenota
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`p-4 rounded-xl border transition-all hover:shadow-md cursor-pointer ${
        service.isElit 
          ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 hover:border-amber-300' 
          : 'bg-white border-gray-100 hover:border-emerald-200'
      }`}
      onClick={handleClick}
      data-testid={`service-card-${service.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-gray-900">{service.name}</h4>
            {service.isElit && <Crown className="w-4 h-4 text-amber-500" />}
          </div>
          {showBusiness && office && (
            <p className="text-sm text-gray-500 mt-0.5">{office?.name}</p>
          )}
          <p className="text-sm text-gray-400 mt-1">{service.duration}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-bold text-emerald-600 text-lg">€{service.price}</p>
          {service.originalPrice && (
            <p className="text-sm text-gray-400 line-through">€{service.originalPrice}</p>
          )}
        </div>
      </div>
      <button 
        onClick={(e) => { e.stopPropagation(); onBook && onBook(service); }}
        className="w-full mt-3 py-2 bg-emerald-500 text-white font-medium rounded-lg hover:bg-emerald-600 transition-colors"
      >
        Prenota ora
      </button>
    </div>
  );
};

const ProductCard = ({ product, showBusiness = false, compact = false, onClick }) => {
  const { addToCart } = useApp();
  const office = OFFICES.find(o => o.id === product.officeId);

  const handleClick = () => {
    if (onClick) onClick();
  };

  if (compact) {
    return (
      <div 
        className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:border-emerald-200 transition-all cursor-pointer"
        onClick={handleClick}
        data-testid={`product-card-${product.id}`}
      >
        <img src={product.image} alt={product.name} className="w-16 h-16 rounded-lg object-cover" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-gray-900 truncate">{product.name}</h4>
            {product.isElit && <Crown className="w-4 h-4 text-amber-500 flex-shrink-0" />}
          </div>
          {showBusiness && office && (
            <p className="text-xs text-gray-500 truncate">{office?.name}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <p className="font-bold text-emerald-600">€{product.price}</p>
            {product.originalPrice && (
              <p className="text-xs text-gray-400 line-through">€{product.originalPrice}</p>
            )}
          </div>
          <button 
            onClick={(e) => { e.stopPropagation(); addToCart(product, 'product'); }}
            className="w-9 h-9 bg-emerald-500 text-white rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors"
            data-testid={`add-product-${product.id}`}
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`rounded-xl border overflow-hidden transition-all hover:shadow-md cursor-pointer ${
        product.isElit ? 'border-amber-200' : 'border-gray-100 hover:border-emerald-200'
      }`}
      onClick={handleClick}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative h-32 sm:h-40">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
        {product.isElit && (
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            <Crown className="w-3 h-3" />
            ELIT
          </div>
        )}
        {product.originalPrice && (
          <div className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            -{Math.round((1 - product.price / product.originalPrice) * 100)}%
          </div>
        )}
      </div>
      <div className="p-3">
        <h4 className="font-medium text-gray-900 text-sm truncate">{product.name}</h4>
        {showBusiness && office && (
          <p className="text-xs text-gray-500 truncate mt-0.5">{office?.name}</p>
        )}
        <div className="flex items-center justify-between mt-2">
          <div>
            <span className="font-bold text-emerald-600">€{product.price}</span>
            {product.originalPrice && (
              <span className="text-xs text-gray-400 line-through ml-1">€{product.originalPrice}</span>
            )}
          </div>
          <button 
            onClick={(e) => { e.stopPropagation(); addToCart(product, 'product'); }}
            className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors"
            data-testid={`add-product-${product.id}`}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const EventCard = ({ event, showBusiness = false, onClick }) => {
  const office = OFFICES.find(o => o.id === event.officeId);
  const spotsLeft = event.maxParticipants - event.currentParticipants;

  return (
    <div 
      className={`rounded-xl border overflow-hidden transition-all hover:shadow-md cursor-pointer ${
        event.isElit ? 'border-amber-200' : 'border-gray-100 hover:border-purple-200'
      }`}
      onClick={onClick}
      data-testid={`event-card-${event.id}`}
    >
      <div className="relative h-36">
        <img src={event.image} alt={event.name} className="w-full h-full object-cover" />
        {event.isElit && (
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            <Crown className="w-3 h-3" />
            ELIT
          </div>
        )}
        <div className="absolute top-2 right-2 bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
          {event.category}
        </div>
        {spotsLeft < 10 && (
          <div className="absolute bottom-2 left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            Ultimi {spotsLeft} posti!
          </div>
        )}
      </div>
      <div className="p-3">
        <h4 className="font-medium text-gray-900 truncate">{event.name}</h4>
        {showBusiness && office && (
          <p className="text-xs text-gray-500 truncate mt-0.5">{office?.name}</p>
        )}
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-purple-500" />
            <span>{new Date(event.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-purple-500" />
            <span>{event.time}</span>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Users className="w-3.5 h-3.5" />
            <span>{event.currentParticipants}/{event.maxParticipants}</span>
          </div>
          <div>
            <span className="font-bold text-purple-600">€{event.price}</span>
            {event.originalPrice && (
              <span className="text-xs text-gray-400 line-through ml-1">€{event.originalPrice}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CourseCard = ({ course, showBusiness = false, onClick }) => {
  const office = OFFICES.find(o => o.id === course.officeId);
  const spotsLeft = course.maxParticipants - course.currentParticipants;

  return (
    <div 
      className={`rounded-xl border overflow-hidden transition-all hover:shadow-md cursor-pointer ${
        course.isElit ? 'border-amber-200' : 'border-gray-100 hover:border-blue-200'
      }`}
      onClick={onClick}
      data-testid={`course-card-${course.id}`}
    >
      <div className="relative h-36">
        <img src={course.image} alt={course.name} className="w-full h-full object-cover" />
        {course.isElit && (
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            <Crown className="w-3 h-3" />
            ELIT
          </div>
        )}
        <div className="absolute top-2 right-2 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
          {course.level}
        </div>
      </div>
      <div className="p-3">
        <h4 className="font-medium text-gray-900 truncate">{course.name}</h4>
        {showBusiness && office && (
          <p className="text-xs text-gray-500 truncate mt-0.5">{office?.name}</p>
        )}
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-blue-500" />
            <span>{course.totalHours}h</span>
          </div>
          <div className="flex items-center gap-1">
            <BookOpen className="w-3.5 h-3.5 text-blue-500" />
            <span>{course.lessons} lezioni</span>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Users className="w-3.5 h-3.5" />
            <span>{spotsLeft} posti</span>
          </div>
          <div>
            <span className="font-bold text-blue-600">€{course.price}</span>
            {course.originalPrice && (
              <span className="text-xs text-gray-400 line-through ml-1">€{course.originalPrice}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { ServiceCard, ProductCard, EventCard, CourseCard };
